// src/services/ValidationService.ts
/**
 * Centralized Validation Service
 * Provides data validation, sanitization, and type checking
 */

import { ErrorLoggerService } from './ErrorLoggerService';

// ==================== TYPES ====================

export interface ValidationRule {
  field: string;
  validators: Validator[];
}

export interface Validator {
  validate: (value: any) => boolean;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
}

// ==================== COMMON VALIDATORS ====================

export const Validators = {
  /**
   * Check if value is required (not null/undefined/empty)
   */
  required: (message: string = 'This field is required'): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    },
    message
  }),

  /**
   * Check if string matches email format
   */
  email: (message: string = 'Invalid email format'): Validator => ({
    validate: (value: any) => {
      if (!value) return true; // Allow empty (use required separately)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(String(value));
    },
    message
  }),

  /**
   * Check if string length is within range
   */
  length: (
    min: number,
    max: number,
    message?: string
  ): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      const length = String(value).length;
      return length >= min && length <= max;
    },
    message: message || `Length must be between ${min} and ${max} characters`
  }),

  /**
   * Check if string has minimum length
   */
  minLength: (min: number, message?: string): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      return String(value).length >= min;
    },
    message: message || `Minimum length is ${min} characters`
  }),

  /**
   * Check if string has maximum length
   */
  maxLength: (max: number, message?: string): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      return String(value).length <= max;
    },
    message: message || `Maximum length is ${max} characters`
  }),

  /**
   * Check if number is within range
   */
  range: (min: number, max: number, message?: string): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined) return true;
      const num = Number(value);
      if (isNaN(num)) return false;
      return num >= min && num <= max;
    },
    message: message || `Value must be between ${min} and ${max}`
  }),

  /**
   * Check if value matches pattern
   */
  pattern: (regex: RegExp, message: string = 'Invalid format'): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      return regex.test(String(value));
    },
    message
  }),

  /**
   * Check if value is a valid URL
   */
  url: (message: string = 'Invalid URL format'): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      try {
        new URL(String(value));
        return true;
      } catch {
        return false;
      }
    },
    message
  }),

  /**
   * Check if value is a valid ISO date
   */
  date: (message: string = 'Invalid date format'): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    message
  }),

  /**
   * Check if value is in allowed list
   */
  oneOf: (allowedValues: any[], message?: string): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined) return true;
      return allowedValues.includes(value);
    },
    message: message || `Value must be one of: ${allowedValues.join(', ')}`
  }),

  /**
   * Custom validator function
   */
  custom: (
    fn: (value: any) => boolean,
    message: string = 'Validation failed'
  ): Validator => ({
    validate: fn,
    message
  }),

  /**
   * Check if value is a valid UUID
   */
  uuid: (message: string = 'Invalid UUID format'): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(String(value));
    },
    message
  }),

  /**
   * Check if value is numeric
   */
  numeric: (message: string = 'Value must be numeric'): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined || value === '') return true;
      return !isNaN(Number(value));
    },
    message
  }),

  /**
   * Check if value is positive number
   */
  positive: (message: string = 'Value must be positive'): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined) return true;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    },
    message
  }),

  /**
   * Check if value is an integer
   */
  integer: (message: string = 'Value must be an integer'): Validator => ({
    validate: (value: any) => {
      if (value === null || value === undefined) return true;
      const num = Number(value);
      return !isNaN(num) && Number.isInteger(num);
    },
    message
  }),

  /**
   * Check if value is a valid phone number (basic check)
   */
  phone: (message: string = 'Invalid phone number'): Validator => ({
    validate: (value: any) => {
      if (!value) return true;
      const phoneRegex = /^\+?[\d\s\-()]+$/;
      const cleaned = String(value).replace(/[\s\-()]/g, '');
      return phoneRegex.test(String(value)) && cleaned.length >= 10 && cleaned.length <= 15;
    },
    message
  })
};

// ==================== VALIDATION SERVICE ====================

export class ValidationService {
  /**
   * Validate data against rules
   */
  static validate(data: Record<string, any>, rules: ValidationRule[]): ValidationResult {
    const errors: Record<string, string[]> = {};

    for (const rule of rules) {
      const value = data[rule.field];
      const fieldErrors: string[] = [];

      for (const validator of rule.validators) {
        if (!validator.validate(value)) {
          fieldErrors.push(validator.message);
        }
      }

      if (fieldErrors.length > 0) {
        errors[rule.field] = fieldErrors;
      }
    }

    const isValid = Object.keys(errors).length === 0;

    // Log validation errors
    if (!isValid) {
      ErrorLoggerService.logValidationError(
        'Validation failed',
        Object.keys(errors).join(', '),
        data,
        'ValidationService'
      );
    }

    return {
      isValid,
      errors
    };
  }

  /**
   * Sanitize string input (prevent XSS).
   *
   * Surrounding whitespace is dropped first. That is not an XSS concern — it is
   * that a value the user typed with a stray space should compare, store and
   * display as the value they meant, and every caller wanted that. The test for
   * it has been failing since the function was written.
   */
  static sanitizeString(input: string): string {
    if (!input) return '';

    return String(input)
      .trim()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Sanitize object by sanitizing all string values
   */
  static sanitizeObject<T extends Record<string, any>>(obj: T): T {
    const sanitized = { ...obj };

    for (const key in sanitized) {
      if (typeof sanitized[key] === 'string') {
        sanitized[key] = this.sanitizeString(sanitized[key]) as any;
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeObject(sanitized[key]);
      }
    }

    return sanitized;
  }

  /**
   * Validate and sanitize input
   */
  static validateAndSanitize<T extends Record<string, any>>(
    data: T,
    rules: ValidationRule[]
  ): { isValid: boolean; data: T; errors: Record<string, string[]> } {
    const validation = this.validate(data, rules);
    const sanitized = this.sanitizeObject(data);

    return {
      isValid: validation.isValid,
      data: sanitized,
      errors: validation.errors
    };
  }

  /**
   * Type guard: check if value is string
   */
  static isString(value: any): value is string {
    return typeof value === 'string';
  }

  /**
   * Type guard: check if value is number
   */
  static isNumber(value: any): value is number {
    return typeof value === 'number' && !isNaN(value);
  }

  /**
   * Type guard: check if value is boolean
   */
  static isBoolean(value: any): value is boolean {
    return typeof value === 'boolean';
  }

  /**
   * Type guard: check if value is array
   */
  static isArray(value: any): value is any[] {
    return Array.isArray(value);
  }

  /**
   * Type guard: check if value is object
   */
  static isObject(value: any): value is Record<string, any> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * Type guard: check if value is null or undefined
   */
  static isNullOrUndefined(value: any): value is null | undefined {
    return value === null || value === undefined;
  }

  /**
   * Safe JSON parse with error handling
   */
  static safeJsonParse<T = any>(jsonString: string, fallback: T): T {
    try {
      return JSON.parse(jsonString) as T;
    } catch (error) {
      console.error('JSON parse error:', error);
      ErrorLoggerService.logValidationError(
        'Invalid JSON format',
        'json',
        jsonString,
        'ValidationService'
      );
      return fallback;
    }
  }

  /**
   * Safe number conversion
   */
  static safeNumber(value: any, fallback: number = 0): number {
    const num = Number(value);
    return isNaN(num) ? fallback : num;
  }

  /**
   * Truncate string to max length
   */
  static truncate(str: string, maxLength: number, suffix: string = '...'): string {
    if (!str || str.length <= maxLength) return str;
    return str.substring(0, maxLength - suffix.length) + suffix;
  }

  /**
   * Deep clone object safely
   */
  static deepClone<T>(obj: T): T {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      console.error('Deep clone error:', error);
      return obj;
    }
  }

  /**
   * Check if two objects are equal (shallow comparison)
   */
  static isEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }
}

// ==================== EXPORTS ====================

export default ValidationService;
